import axios from 'axios';

const activeRequests = new Map();

export class ApiError extends Error {
  constructor(message, { status = 0, code = 'REQUEST_FAILED', errors = null, cause = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.errors = errors;
    this.cause = cause;
    this.response = cause?.response;
    this.request = cause?.request;
    this.config = cause?.config;
  }
}

export const getApiErrorMessage = (error, fallback = 'Operation failed') => {
  if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return '';
  const detail = error?.response?.data?.detail;
  return error?.response?.data?.message
    || (typeof detail === 'string' ? detail : detail?.message)
    || error?.message
    || fallback;
};

export const normalizeApiError = (error) => {
  if (error instanceof ApiError || axios.isCancel(error) || error?.code === 'ERR_CANCELED') return error;
  const status = Number(error?.response?.status || 0);
  const codeByStatus = {
    400: 'VALIDATION_ERROR', 401: 'SESSION_EXPIRED', 403: 'ACCESS_DENIED',
    404: 'NOT_FOUND', 409: 'CONFLICT', 422: 'BUSINESS_VALIDATION',
    500: 'SERVER_ERROR', 503: 'SERVICE_UNAVAILABLE'
  };
  return new ApiError(getApiErrorMessage(error), {
    status,
    code: codeByStatus[status] || (error?.request ? 'NETWORK_ERROR' : 'REQUEST_FAILED'),
    errors: error?.response?.data?.errors || null,
    cause: error
  });
};

export const cancelManagedRequest = (key, reason = 'Superseded by a newer request') => {
  const controller = activeRequests.get(key);
  if (controller) {
    controller.abort(reason);
    activeRequests.delete(key);
  }
};

export const prepareManagedRequest = (config = {}) => {
  const requestKey = config.requestKey;
  if (!requestKey) return config;
  cancelManagedRequest(requestKey);
  const controller = new AbortController();
  activeRequests.set(requestKey, controller);
  return { ...config, signal: config.signal || controller.signal, __managedRequestKey: requestKey, __managedRequestController: controller };
};

export const releaseManagedRequest = (config = {}) => {
  const requestKey = config.__managedRequestKey;
  const controller = config.__managedRequestController;
  if (requestKey && activeRequests.get(requestKey) === controller) activeRequests.delete(requestKey);
};
